import { lazy, Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppRouter from './router';
import BaseLayout from '@/layouts/BaseLayout';
import DemoRatioApplier from '@/components/demo/DemoRatioApplier';
import BackgroundObjects from '@/components/BackgroundObjects';
import { DemoProvider } from '@/contexts/DemoContext';
import { AuthModalProvider } from '@/contexts/AuthModalContext';
import { AnimationProvider } from '@/contexts/AnimationContext';
import { VFXConfigProvider } from '@/features/vfx/contexts/VFXConfigContext';
import VFXCanvas from '@/features/vfx/components/VFXCanvas';
import BackgroundLayer from '@/features/vfx/components/BackgroundLayer';
import { useVFXConfig } from '@/features/vfx/hooks/useVFXConfig';
import { useAutoRedirect } from '@/hooks/useAutoRedirect';
import AutoLogoutWarning from '@/components/AutoLogoutWarning';
import PendingTransactionFloater from '@/features/finance/components/PendingTransactionFloater';
import SideStrip from '@/features/help/components/SideStrip';
import { TourProvider } from '@/components/TourGuide';
import KioskInAppPopupGate from '@/components/KioskInAppPopupGate';
import GamifyWidgetMount from '@/components/GamifyWidgetMount';
import { BackgroundThemeSync } from '@/hooks/useBackgroundThemeAttr';
import { STYLE_SWITCHER_BUILD } from '@/config/uiStyleEnv';

/**
 * UI style preview switcher, only where isStyleSwitcherAllowed() (dev, or a build with
 * VITE_UI_STYLE_SWITCHER=true). Gated on the build-time constant behind it, so any other build
 * compiles the switcher out.
 */
const StyleSwitcher = STYLE_SWITCHER_BUILD ? lazy(() => import('@/dev/StyleSwitcher')) : null;

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <VFXConfigProvider>
        <BackgroundThemeSync />
        <DemoProvider>
          <BrowserRouter>
            <AnimationProvider>
              <AuthModalProvider>
                <DemoRatioApplier>
                  <BaseLayout>
                    <BackgroundObjects />
                    <TourProvider>
                      <AppRouter />
                      {/* Right-edge help / app strip (non-kiosk only); before the floater so the
                          floater's open panel paints over it (src/layouts/edgeDock.ts). */}
                      <SideStrip />
                      <PendingTransactionFloater />
                      <KioskInAppPopupGate />
                      <GamifyWidgetMount />
                    </TourProvider>
                    {StyleSwitcher ? (
                      <Suspense fallback={null}>
                        <StyleSwitcher />
                      </Suspense>
                    ) : null}
                  </BaseLayout>
                </DemoRatioApplier>
              </AuthModalProvider>
            </AnimationProvider>
          </BrowserRouter>
        </DemoProvider>
      </VFXConfigProvider>
    </QueryClientProvider>
  );
}

export default App;

